<template>
    <v-dialog
        v-model="dialog"
        max-width="600"
        persistent
    >
        <v-card>
            <v-card-title>
                <span class="headline">{{ dialog_title }}</span>
            </v-card-title>

            <v-card-text>
                <template v-if="app?.app_permissions?.length">
                    <p class="text-body-1 mb-2">
                        By proceeding to {{ action_text.toLowerCase() }} <strong>{{ app_label }}</strong>, you grant
                        it the following permissions:
                    </p>

                    <app-permissions-list
                        :permissions="app.app_permissions"
                        list_class="pa-0 mb-4"
                    />
                </template>

                <template v-else>
                    <p class="mb-4">
                        You are about to {{ action_text.toLowerCase() }} <strong>{{ app_label }}</strong>.
                    </p>
                </template>

                <v-card
                    v-if="app_price_is_set"
                    outlined
                    class="mb-4"
                >
                    <v-card-text class="pb-2">
                        <h4 class="text-subtitle-1 font-weight-bold">
                            Pricing Information
                        </h4>

                        <div
                            v-if="app_is_free"
                            class="text-center"
                        >
                            <div class="text-h6 primary--text font-weight-bold">Free</div>
                            <p class="text-body-2 grey--text mb-3">
                                This app is free to install and use. No charges will apply.
                            </p>
                        </div>

                        <div v-else>
                            <v-alert
                                v-if="is_upgrade"
                                border="left"
                                colored-border
                                color="primary"
                                dense
                                class="mt-4 mb-2 shaded-light"
                            >
                                <div class="text-body-2">
                                    Billing will continue uninterrupted at your current monthly rate of
                                    {{ app_monthly_fee_pretty }}. No additional charges will apply.
                                </div>
                            </v-alert>

                            <template v-if="!is_upgrade">
                                <div class="d-flex justify-space-between align-center mb-1">
                                    <span class="text-body-2">Monthly Fee:</span>
                                    <span class="text-h6 primary--text font-weight-bold">{{ app_monthly_fee_pretty
                                    }}</span>
                                </div>

                                <div
                                    v-if="app_has_free_trial"
                                    class="d-flex justify-space-between align-center mb-3"
                                >
                                    <span class="text-body-2">Free Trial Period:</span>
                                    <span class="text-body-2 font-weight-medium">{{ app.free_trial_days_remaining }}
                                        days</span>
                                </div>

                                <v-alert
                                    border="left"
                                    colored-border
                                    color="primary"
                                    dense
                                    class="shaded-light mb-2 px-4 py-3"
                                >
                                    <div class="text-body-2">
                                        <strong>What happens next:</strong>
                                        <span v-if="app_has_free_trial">
                                            You will be charged {{ app_monthly_fee_pretty }} after the
                                            {{ app.free_trial_days_remaining }}-day free trial period ends. No payment
                                            is due until then, and you can cancel any time during the trial.
                                        </span>
                                        <span v-else>
                                            You will be charged {{ app_monthly_fee_pretty }} monthly starting
                                            immediately.
                                        </span>
                                    </div>
                                </v-alert>
                            </template>

                        </div>
                    </v-card-text>
                </v-card>

                <m-legal-agreement
                    v-if="app_price_is_set && !app_is_free"
                    ref="agreement"
                    type="app-store-fee-agreement"
                    :app_id="app.app_id"
                    :organization_id="$rc.$state.getOrganizationId()"
                    v-model="agree_to_terms"
                    class="mt-0"
                />

                <v-alert
                    v-if="!$rc.$state.isOrganizationAdmin()"
                    type="error"
                    class="mt-4"
                    dense
                    outlined
                >
                    <div class="text-body-2">
                        <strong>Admin Access Required:</strong>
                        Only organization administrators can install or update apps. Please contact your
                        organization admin to proceed.
                    </div>
                </v-alert>
            </v-card-text>

            <v-card-actions>
                <v-spacer />

                <v-btn
                    text
                    @click="onCancel"
                >
                    Cancel
                </v-btn>

                <v-btn
                    color="primary"
                    @click="onConfirm"
                    :disabled="!can_proceed"
                >
                    <v-icon
                        size="20"
                        class="mr-1"
                        aria-hidden="true"
                    >{{ action_icon }}</v-icon>
                    {{ action_text }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import AppPermissionsList from '@/components/app-store/app-permissions-list.vue';
import AppInstallation from '@/mixins/app-installation.js';
import AppDerived from '@/mixins/app-derived.js';

export default {
    mixins: [AppInstallation, AppDerived],

    components: {
        AppPermissionsList,
    },

    props: {
        value: {
            type: Boolean,
            default: false,
        },

        app: {
            type: Object,
            required: true,
        },

        is_upgrade: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            agree_to_terms: false,
        };
    },

    computed: {
        dialog: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            },
        },

        can_proceed() {
            if (!this.$rc.$state.isOrganizationAdmin()) return false;
            if (!this.app_price_is_set) return true;
            if (this.app_is_free) return true;
            if (this.is_upgrade) return true;
            return this.agree_to_terms;
        },

        dialog_title() {
            return this.is_upgrade ? 'Confirm App Update' : 'Confirm Installation';
        },

        action_text() {
            return this.is_upgrade ? 'Update' : 'Install';
        },

        action_icon() {
            return this.is_upgrade ? 'mdi-update' : 'mdi-download';
        },
    },

    watch: {
        value(newVal) {
            if (newVal) {
                this.agree_to_terms = false;
            }
        },
    },

    methods: {
        getAgreementSpecimen() {
            return this.$refs.agreement?.getSpecimen?.() || null;
        },

        onCancel() {
            this.$emit('input', false);
        },

        onConfirm() {
            this.$emit('confirm', { app: this.app, agreement_specimen: this.getAgreementSpecimen() });
            this.$emit('input', false);
        },
    },
};
</script>

<style lang="scss" scoped></style>